import { useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  NotProvisionedError,
  useAcceptLegalDocument,
  useLegalAcceptance,
} from "../api/hooks/useLegalAcceptance";
import { LEGAL_DOCUMENTS, type LegalDocumentKey } from "../pages/legal/legalContent";

// Web half of the legal acceptance gate (L1-U3, ADR 0028 D4). When the server
// reports enforcement on and the user has not accepted every current document,
// this replaces the app with an accept screen until all documents are accepted.
// The server-side JWT gate is the real boundary; this is the cooperative UI that
// lets the user clear it.
//
// Also the web half of the not-provisioned refusal (ADR 0030 D3): the status
// query is the SPA's earliest authenticated API call, so a definitive 403
// `not_provisioned` here means this Auth0 identity has no account -- show the
// no-access screen instead of letting every widget fail opaquely.
export function AcceptanceGate({ children }: { children: ReactNode }) {
  const status = useLegalAcceptance();
  const accept = useAcceptLegalDocument();

  if (status.error instanceof NotProvisionedError) {
    return <NoAccessScreen />;
  }

  // Render the app while the status is loading or on a transient fetch failure
  // (fail open) and whenever enforcement is off -- the gate is strictly additive,
  // so beta behaviour is unchanged. Only a definitive "enforced and not fully
  // accepted" status replaces the app with the accept screen. The server-side
  // JWT gate is the real boundary, so failing open here leaks nothing.
  if (!status.data || !status.data.enforced || status.data.all_accepted) {
    return <>{children}</>;
  }

  return (
    <main className="min-h-screen bg-surface">
      <section
        role="dialog"
        aria-label="Accept legal documents"
        className="mx-auto max-w-2xl px-4 py-12 space-y-6"
      >
        <header className="space-y-1">
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Before you continue
          </h1>
          <p className="text-sm text-on-surface-variant">
            Please review and accept the following to keep using WickScope.
          </p>
        </header>
        <Card>
          <CardContent className="divide-y divide-outline-variant p-0">
            {status.data.documents.map((doc) => {
              const content = LEGAL_DOCUMENTS[doc.document as LegalDocumentKey];
              return (
                <div
                  key={doc.document}
                  className="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="space-y-0.5">
                    <a
                      href={content.path}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-primary underline"
                    >
                      {content.title}
                    </a>
                    <p className="text-xs text-on-surface-variant">{content.summary}</p>
                  </div>
                  {doc.accepted ? (
                    <span
                      className="text-sm text-on-surface-variant"
                      aria-label={`${content.title} accepted`}
                    >
                      Accepted
                    </span>
                  ) : (
                    <Button
                      type="button"
                      disabled={accept.isPending}
                      onClick={() =>
                        accept.mutate({
                          document: doc.document,
                          version: doc.current_version,
                        })
                      }
                    >
                      Accept
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
        {accept.isError ? (
          <p role="alert" className="text-sm text-error">
            Could not record your acceptance. Please try again.
          </p>
        ) : null}
      </section>
    </main>
  );
}

// No-access screen (ADR 0030 D3): the identity is authenticated with Auth0
// but has no account here (uninvited sign-in during the private beta). The
// server refuses every API call with 403 not_provisioned, so there is nothing
// to render behind this -- offer the waitlist and a clean sign-out.
function NoAccessScreen() {
  const { logout } = useAuth0();
  return (
    <main className="min-h-screen bg-surface">
      <section
        role="alert"
        aria-label="No beta access"
        className="mx-auto max-w-xl px-4 py-16 space-y-4"
      >
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          You&apos;re not part of the beta yet
        </h1>
        <p className="text-sm text-on-surface-variant">
          This account isn&apos;t set up for the WickScope private beta. If you
          received an invite email, sign in with that exact account. Otherwise,
          join the waitlist and we&apos;ll let you know when a spot opens up.
        </p>
        <div className="flex items-center gap-3">
          <Button asChild type="button">
            <a href="/">Join the waitlist</a>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void logout({ logoutParams: { returnTo: window.location.origin } });
            }}
          >
            Sign out
          </Button>
        </div>
      </section>
    </main>
  );
}
